import { useReducer, useState, useEffect } from 'react'

import type { ReactElement } from 'react'
import type { IAppManagementByDeviceType } from '~/types/application-management'
import type { IMacroApp } from '~/types/review'

import Button from '~/components/Buttons/Button'
import CollapseButton from '~/components/Buttons/CollapseButton'
import Filters, { FiltersContent } from '~/components/Filters'
import MultiSelectInput from '~/components/Inputs/Basic/MultiSelectInput'
import CustomAppOption from '~/components/SelectOptions/CustomAppOption'
import { FASTLY_PATH } from '~/const/asset'
import { useAppsManagementListQuery } from '~/queries/application-management'
import { splitByDeviceType } from '~/utils/application'
import { resize } from '~/utils/media'

import './styles.scss'

type ActionType = 'applications' | 'is_all_apps' | 'reset'

type Actions = {
  type: ActionType
  value?: string[] | boolean
}

export interface FiltersState {
  applications: string[]
  is_all_apps: boolean
}

type ReducerState = (state: FiltersState, action: Actions) => FiltersState

export const applicationFilterFn =
  (filters?: FiltersState) =>
  <T extends { applications: IMacroApp[] | null; is_all_apps: boolean }>({ applications, is_all_apps }: T): boolean => {
    const isAllApps = filters?.is_all_apps

    const applicationsBelongsToFilter =
      !filters?.is_all_apps &&
      (is_all_apps ||
        !!applications?.some(({ app_key: appAppKey, device_type: appDeviceType }) => {
          return filters?.applications.every((appParams) => {
            const { app_key, device_type } = JSON.parse(appParams)
            return appAppKey === app_key && appDeviceType === device_type
          })
        }))

    const noFilter = filters?.applications.length === 0

    if (isAllApps) {
      return is_all_apps
    }

    return noFilter || applicationsBelongsToFilter
  }

const reducer = (state: FiltersState, action: Actions) => {
  switch (action.type) {
    case 'is_all_apps':
      return { ...state, is_all_apps: action.value as boolean }
    case 'applications':
      return { ...state, applications: (action.value as string[]) || [] }
    case 'reset':
      return initialState
    default:
      return state
  }
}

const initialState: FiltersState = {
  applications: [],
  is_all_apps: false,
}

const getOptionFromFilters = (filterValues: string[], options: IOption[]): IOption[] => {
  return options.filter(({ value }) => filterValues.includes(value))
}

interface Props {
  onFiltersChange: (filterState: FiltersState) => void
}

const TopicManagementFilters = ({ onFiltersChange }: Props): ReactElement => {
  /** Local state */

  const [state, dispatch] = useReducer<ReducerState>(reducer, initialState)

  const [isFiltersCollapsed, setIsFiltersCollapsed] = useState(false)

  /** Queries & Mutations */

  const { data: apps, isLoading: isAppsLoading } = useAppsManagementListQuery()

  /** Effects */

  useEffect(() => {
    onFiltersChange(state)
  }, [onFiltersChange, state])

  /** Handlers */

  const toggleCollapseFilters = () => setIsFiltersCollapsed(!isFiltersCollapsed)

  const handleFilterChanged = (filter: ActionType, options: string[]) => {
    if (filter === 'applications') {
      dispatch({ type: 'is_all_apps', value: options.length === apps?.length })
    }
    dispatch({ type: filter, value: options })
  }

  const handleResetAll = () => dispatch({ type: 'reset' })

  const applicationOptions =
    apps
      ?.reduce(splitByDeviceType(), [] as IAppManagementByDeviceType[])
      .map(({ app_key, default_display_name, device_type, icon_file_id }) => ({
        device: device_type,
        label: default_display_name,
        url: icon_file_id ? resize(`${FASTLY_PATH}${icon_file_id}`, 32, 32) : undefined,
        value: JSON.stringify({
          app_key,
          device_type,
        }),
      }))
      .sort(({ label: aName }, { label: bName }) => aName.localeCompare(bName)) || []

  /** Render */

  return (
    <Filters className="reviews-management-filters">
      {!isFiltersCollapsed ? (
        <FiltersContent>
          <div>
            <MultiSelectInput
              className="filter filter-applications"
              components={{ Option: CustomAppOption }}
              isLoading={isAppsLoading}
              label="Applications"
              onChange={(value: unknown) =>
                handleFilterChanged(
                  'applications',
                  (value as IOption[]).map(({ value }) => value)
                )
              }
              options={applicationOptions}
              placeholder="All applications"
              value={getOptionFromFilters(state.applications, applicationOptions)}
            />
          </div>
          <div>
            <Button className="reset-button" ghost onClick={handleResetAll}>
              Reset all
            </Button>
          </div>
        </FiltersContent>
      ) : null}
      <CollapseButton isCollapsed={isFiltersCollapsed} onClick={toggleCollapseFilters} />
    </Filters>
  )
}

export default TopicManagementFilters
